import { DataTable } from "@/components/DataTable"
import { DetailShell } from "@/components/DetailShell"
import { Field } from "@/components/Field"
import { Link } from "@/components/Link"
import { Section } from "@/components/Section"
import { client } from "@/lib/client"
import { dash, fullName } from "@/lib/utils"
import { useApi, type Nav } from "@/lib/hooks"

export function JudgeDetail({ id, nav }: { id: string; nav: Nav }) {
  const { data, err } = useApi(() => client.api.judges[":id"].$get({ param: { id } }), [id])
  const p = data?.person
  return (
    <DetailShell title={p ? dash(fullName(p)) : "…"} backTo="/judges" nav={nav} data={data} err={err}>
      {data && p && (
        <div className="space-y-6">
          <div className="space-y-1">
            <Field label="Born" value={dash(p.dateDob)} />
            <Field label="Birthplace" value={dash([p.dobCity, p.dobState, p.dobCountry].filter(Boolean).join(", "))} />
            <Field label="Died" value={dash(p.dateDod)} />
            <Field label="Deathplace" value={dash([p.dodCity, p.dodState, p.dodCountry].filter(Boolean).join(", "))} />
            <Field label="Gender" value={dash(p.gender)} />
            <Field label="Religion" value={dash(p.religion)} />
            <Field label="Race" value={dash(data.races.map((r) => r.race).join(", "))} />
            <Field label="FJC ID" value={dash(p.fjcId)} />
          </div>

          <Section title="Positions">
            <DataTable
              headers={["Title", "Court", "Type", "Nominated", "Confirmed", "Start", "Terminated", "Reason", "Votes"]}
              rows={data.positions.map((r) => [
                dash(r.jobTitle),
                r.courtId ? (
                  <Link to={`/courts/${r.courtId}`} nav={nav}>
                    {dash(r.courtName)}
                  </Link>
                ) : (
                  dash(r.courtName)
                ),
                dash(r.positionType),
                dash(r.dateNominated),
                dash(r.dateConfirmation),
                dash(r.dateStart),
                dash(r.dateTermination),
                dash(r.terminationReason),
                r.votesYes != null || r.votesNo != null ? `${r.votesYes ?? "?"}–${r.votesNo ?? "?"}` : "—",
              ])}
            />
          </Section>

          <Section title="Education">
            <DataTable
              headers={["School", "Level", "Detail", "Year"]}
              rows={data.education.map((r) => [dash(r.school), dash(r.degreeLevel), dash(r.degreeDetail), dash(r.degreeYear)])}
            />
          </Section>

          <Section title="Political affiliations">
            <DataTable
              headers={["Party", "Source", "Start", "End"]}
              rows={data.affiliations.map((r) => [dash(r.politicalParty), dash(r.source), dash(r.dateStart), dash(r.dateEnd)])}
            />
          </Section>

          <Section title="ABA ratings">
            <DataTable
              headers={["Rating", "Year"]}
              rows={data.abaRatings.map((r) => [dash(r.rating), dash(r.yearRated)])}
            />
          </Section>

          <Section title="Sources">
            <DataTable
              headers={["URL", "Notes", "Accessed"]}
              rows={data.sources.map((r) => [
                r.url ? (
                  <a className="text-primary hover:underline" href={r.url} target="_blank" rel="noreferrer">
                    {r.url}
                  </a>
                ) : (
                  "—"
                ),
                dash(r.notes),
                dash(r.dateAccessed),
              ])}
            />
          </Section>

          <Section title="Financial disclosures">
            <DataTable
              headers={["Year", "Report type", "Amended", "Pages", "File"]}
              rows={data.disclosures.map((r) => [
                dash(r.year),
                dash(r.reportType),
                r.isAmended ? "Yes" : "No",
                dash(r.pageCount),
                dash(r.filepath),
              ])}
            />
          </Section>
        </div>
      )}
    </DetailShell>
  )
}
